// DignityBriefingPip — bottom-right picture-in-picture player for the
// per-hospital Dignity briefing video (2026-09-15).
//
// Lives on the hospital page across every tab. When a briefing exists it
// starts as a small launcher pill above the tab bar; opening it plays the
// video (YouTube via youtube-nocookie, or an MP4 we host) in a 16:9 dock
// that can widen or collapse back to the pill. Also emits the VideoObject
// JSON-LD for the briefing so the page carries the video's metadata whether
// or not the visitor opens it.
//
// The visitor's dock size and dismissal are remembered per session so the
// pill doesn't nag while they read.
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Maximize2, Minimize2, Minus, Play, X } from "lucide-react";
import { briefingPoster, isoDuration, type HospitalBriefingVideo } from "@/hooks/useHospitalBriefingVideo";

// Matches the global bottom tab bar the narration overlay also clears.
const TAB_BAR_H = 80;
const sans = "'Inter', system-ui, -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

export interface DignityBriefingPipProps {
  hospitalId: string;
  hospitalName: string;
  video: HospitalBriefingVideo;
  /** Set true by a "Play Dignity's briefing" control elsewhere on the page. */
  requestOpen?: number;
  isDark: boolean;
}

type Mode = "pill" | "dock" | "wide" | "hidden";

export function DignityBriefingPip({ hospitalId, hospitalName, video, requestOpen, isDark }: DignityBriefingPipProps) {
  const { t } = useTranslation(["hospital"]);
  const tr = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    t(`hospital:briefingPip.${key}`, { defaultValue: fallback, ...(opts || {}) });
  const storageKey = `ahp_briefing_pip_${hospitalId}`;
  const [mode, setMode] = useState<Mode>(() => {
    try { const v = sessionStorage.getItem(storageKey); if (v === "hidden") return "hidden"; } catch { /* private mode */ }
    return "pill";
  });
  // Autoplay only when the visitor asked for the video (dock opened by a
  // click), never on page load.
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!requestOpen) return;
    setMode((m) => (m === "wide" ? "wide" : "dock"));
    setPlaying(true);
  }, [requestOpen]);
  useEffect(() => {
    try { if (mode === "hidden") sessionStorage.setItem(storageKey, "hidden"); else sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
  }, [mode, storageKey]);
  // Esc collapses an open dock to the pill.
  useEffect(() => {
    if (mode !== "dock" && mode !== "wide") return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setMode("pill"); setPlaying(false); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mode]);

  const title = video.title || tr("defaultTitle", "Dignity's briefing on {{name}}", { name: hospitalName });
  const description = video.description || tr("defaultDescription", "Dignity walks through {{name}}'s quality report card — CMS Care Compare ratings, patient survey results, and published prices — as of the CMS release it was recorded against.", { name: hospitalName });
  const poster = briefingPoster(video);
  const dateLabel = video.publishedAt
    ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(video.publishedAt) ? `${video.publishedAt}T00:00:00` : video.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const durationLabel = video.durationSec ? `${Math.floor(video.durationSec / 60)}:${String(Math.round(video.durationSec % 60)).padStart(2, "0")}` : null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title,
    description,
    ...(poster ? { thumbnailUrl: [poster] } : {}),
    ...(video.publishedAt ? { uploadDate: video.publishedAt } : {}),
    ...(isoDuration(video.durationSec) ? { duration: isoDuration(video.durationSec) } : {}),
    ...(video.mp4Url ? { contentUrl: video.mp4Url } : {}),
    ...(video.youtubeId ? { embedUrl: `https://www.youtube-nocookie.com/embed/${video.youtubeId}` } : {}),
    publisher: { "@type": "Organization", name: "American Hospital Prices", url: "https://americanhospitalprices.com" },
    about: { "@type": "Hospital", name: hospitalName, url: `https://americanhospitalprices.com/hospital/${hospitalId}` },
  };

  const P = isDark ? {
    bg: "linear-gradient(180deg, rgba(2,22,60,0.97) 0%, rgba(0,14,44,0.99) 100%)",
    border: "rgba(var(--civic-gold-rgb), 0.35)",
    text: "var(--civic-parchment)",
    muted: "rgba(var(--civic-parchment-rgb), 0.6)",
    gold: "var(--civic-gold)",
    shadow: "0 18px 48px -16px rgba(0,0,0,0.75)",
    btn: "rgba(var(--civic-parchment-rgb), 0.08)",
  } : {
    bg: "linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)",
    border: "rgba(var(--civic-navy-deeper-rgb), 0.18)",
    text: "var(--civic-navy-deeper)",
    muted: "rgba(var(--civic-navy-deeper-rgb), 0.62)",
    gold: "var(--civic-gold-ink)",
    shadow: "0 18px 48px -18px rgba(var(--civic-navy-deeper-rgb), 0.45)",
    btn: "rgba(var(--civic-navy-deeper-rgb), 0.06)",
  };
  const bottom = `calc(${TAB_BAR_H + 12}px + env(safe-area-inset-bottom, 0px))`;
  const iconBtn = (label: string, onClick: () => void, icon: React.ReactNode) => (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      style={{ width: 26, height: 26, borderRadius: 6, border: 0, background: P.btn, color: P.text, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      {icon}
    </button>
  );

  const helmet = (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );

  if (mode === "hidden") return helmet;

  if (mode === "pill") {
    return (
      <>
        {helmet}
        <div style={{ position: "fixed", right: 16, bottom, zIndex: 9990, display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            onClick={() => { setMode("dock"); setPlaying(true); }}
            className="transition-transform hover:-translate-y-0.5"
            style={{
              display: "inline-flex", alignItems: "center", gap: 10, padding: "8px 14px 8px 8px", borderRadius: 999,
              background: P.bg, border: `1px solid ${P.border}`, boxShadow: P.shadow, color: P.text, cursor: "pointer", fontFamily: sans,
            }}
          >
            <span style={{ position: "relative", width: 36, height: 36, borderRadius: "50%", overflow: "hidden", background: "rgba(var(--civic-navy-rgb),0.12)", flexShrink: 0 }}>
              <img src="/consumer/dignity-transparent.webp" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <span style={{ position: "absolute", right: -2, bottom: -2, width: 16, height: 16, borderRadius: "50%", background: "var(--civic-gold)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Play style={{ width: 8, height: 8, marginLeft: 1, color: "#0a1628" }} fill="currentColor" />
              </span>
            </span>
            <span style={{ textAlign: "left", minWidth: 0 }}>
              <span style={{ display: "block", fontFamily: serif, fontSize: 14, lineHeight: 1.15 }}>{tr("pillTitle", "Dignity's briefing")}</span>
              <span style={{ display: "block", fontSize: 10.5, color: P.muted, lineHeight: 1.2, marginTop: 2 }}>
                {[durationLabel, dateLabel ? tr("asOf", "as of {{date}}", { date: dateLabel }) : null].filter(Boolean).join(" · ") || tr("pillSub", "Watch the report card")}
              </span>
            </span>
          </button>
          <button type="button" onClick={() => setMode("hidden")} aria-label={tr("dismiss", "Dismiss for this visit")} title={tr("dismiss", "Dismiss for this visit")}
            style={{ width: 24, height: 24, borderRadius: "50%", border: `1px solid ${P.border}`, background: P.bg, color: P.muted, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: P.shadow }}>
            <X style={{ width: 12, height: 12 }} />
          </button>
        </div>
      </>
    );
  }

  const wide = mode === "wide";
  const width = wide ? "min(720px, calc(100vw - 32px))" : "min(360px, calc(100vw - 32px))";
  const embedSrc = video.youtubeId
    ? `https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1${playing ? "&autoplay=1" : ""}`
    : null;

  return (
    <>
      {helmet}
      <section
        aria-label={title}
        style={{
          position: "fixed", right: 16, bottom, zIndex: 9990, width,
          background: P.bg, border: `1px solid ${P.border}`, borderRadius: 12, boxShadow: P.shadow, overflow: "hidden",
          color: P.text, fontFamily: sans,
        }}
      >
        <div aria-hidden style={{ height: 3, background: "linear-gradient(90deg, var(--civic-red) 0 33%, var(--civic-parchment, #F4EEDC) 33% 66%, var(--civic-navy) 66%)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 8px 8px 10px" }}>
          <img src="/consumer/dignity-transparent.webp" alt="" style={{ width: 26, height: 26, objectFit: "contain", flexShrink: 0 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontFamily: serif, fontSize: 14, lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={title}>{title}</div>
            <div style={{ fontSize: 10.5, color: P.muted, lineHeight: 1.2, marginTop: 1 }}>
              {[dateLabel ? tr("asOf", "as of {{date}}", { date: dateLabel }) : null, durationLabel, video.mp4Url ? tr("hosted", "hosted by AHP") : tr("youtube", "YouTube")].filter(Boolean).join(" · ")}
            </div>
          </div>
          {iconBtn(wide ? tr("shrink", "Smaller") : tr("widen", "Larger"), () => setMode(wide ? "dock" : "wide"), wide ? <Minimize2 style={{ width: 13, height: 13 }} /> : <Maximize2 style={{ width: 13, height: 13 }} />)}
          {iconBtn(tr("minimize", "Minimize"), () => { setMode("pill"); setPlaying(false); }, <Minus style={{ width: 13, height: 13 }} />)}
          {iconBtn(tr("close", "Close"), () => { setMode("hidden"); setPlaying(false); }, <X style={{ width: 13, height: 13 }} />)}
        </div>
        <div style={{ position: "relative", aspectRatio: "16 / 9", background: "#000" }}>
          {video.mp4Url ? (
            <video
              src={video.mp4Url}
              poster={poster || undefined}
              controls
              autoPlay={playing}
              playsInline
              preload="metadata"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            />
          ) : embedSrc ? (
            <iframe
              src={embedSrc}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
            />
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 10px 8px", fontSize: 10.5, color: P.muted }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tr("footer", "Numbers in the briefing are as of its recording date — the card above is live.")}</span>
          <a href={`/hospital/${hospitalId}/quality`} style={{ color: P.gold, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>{tr("openQuality", "Report card →")}</a>
        </div>
      </section>
    </>
  );
}
